import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function setActiveLayer(layers, index, { instant = false } = {}) {
  layers.forEach((layer, i) => {
    const isActive = i === index;
    layer.toggleAttribute("aria-hidden", !isActive);
    layer.classList.toggle("opacity-0", !isActive);

    gsap.to(layer, {
      autoAlpha: isActive ? 1 : 0,
      duration: instant ? 0 : 1,
      ease: "power2.out",
      overwrite: "auto",
    });
  });
}

function animateNumberEl(el, value, { instant = false } = {}) {
  if (!el || el.dataset.current === String(value)) return;
  el.dataset.current = String(value);

  if (instant) {
    el.textContent = String(value);
    gsap.set(el, { yPercent: 0, autoAlpha: 1 });
    return;
  }

  gsap
    .timeline({ defaults: { ease: "power2.out" } })
    .to(el, { yPercent: -35, autoAlpha: 0, duration: 0.2 })
    .add(() => {
      el.textContent = String(value);
    })
    .fromTo(
      el,
      { yPercent: 35, autoAlpha: 0 },
      { yPercent: 0, autoAlpha: 1, duration: 0.3 },
    );
}

function activateStep(layers, numberEl, index, { instant = false, animateNumber = true } = {}) {
  setActiveLayer(layers, index, { instant });
  if (animateNumber && numberEl) {
    animateNumberEl(numberEl, index + 1, { instant });
  }
}

function initScrollyReveal(root) {
  const scrolly = root.querySelector("[data-scrolly-scrolly]");
  const heading = root.querySelector("[data-scrolly-heading]");
  const numberEl = root.querySelector("[data-scrolly-number]");
  const layers = gsap.utils.toArray(root.querySelectorAll("[data-scrolly-layer]"));
  const panels = gsap.utils.toArray(root.querySelectorAll("[data-scrolly-panel]"));

  if (!scrolly || layers.length === 0 || panels.length === 0) return;

  gsap.set(layers.slice(1), { autoAlpha: 0 });
  gsap.set(layers[0], { autoAlpha: 1 });
  if (numberEl) {
    numberEl.textContent = "1";
    numberEl.dataset.current = "1";
  }

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const createTriggers = (isDesktop) => {
    const triggers = [];
    let activeIndex = -1;
    const instant = reduceMotion.matches;

    const runActivate = (index) => {
      if (index === activeIndex) return;
      activeIndex = index;
      activateStep(layers, numberEl, index, {
        instant,
        animateNumber: isDesktop,
      });
    };

    if (isDesktop) {
      const updateDesktopLayout = () => {
        const headingHeight = heading?.offsetHeight ?? 0;
        if (heading) {
          scrolly.style.setProperty("--scrolly-heading-height", `${headingHeight}px`);
        }

        const visual = scrolly.querySelector("[data-scrolly-visual]");
        const stickyEl = visual?.firstElementChild;
        const imageFrame = layers[0]?.parentElement;
        const lastPanel = panels[panels.length - 1];
        const lastCopy = lastPanel?.querySelector(".border-t");
        if (!stickyEl || !imageFrame || !lastCopy) return;

        // Match the gap above the last step to the gap between earlier steps,
        // without restoring the empty tail under the last one.
        if (lastPanel && panels.length >= 2) {
          lastPanel.style.paddingTop = "";
          const copies = panels.map((panel) => panel.querySelector(".border-t"));
          if (copies.every(Boolean)) {
            const targetGap = (() => {
              if (panels.length >= 3) {
                return (
                  copies[1].getBoundingClientRect().top -
                  copies[0].getBoundingClientRect().bottom
                );
              }
              const firstPanel = panels[0].getBoundingClientRect();
              return (
                firstPanel.height -
                (copies[0].getBoundingClientRect().height +
                  copies[1].getBoundingClientRect().height) /
                  2
              );
            })();
            const currentGap =
              copies.at(-1).getBoundingClientRect().top -
              copies.at(-2).getBoundingClientRect().bottom;
            const pad = parseFloat(getComputedStyle(lastPanel).paddingTop) || 0;
            lastPanel.style.paddingTop = `${Math.max(0, pad + targetGap - currentGap)}px`;
          }
        }

        const scrollyBottom = scrolly.getBoundingClientRect().bottom;
        const copyRect = lastCopy.getBoundingClientRect();
        const stickyRect = stickyEl.getBoundingClientRect();
        const imageRect = imageFrame.getBoundingClientRect();
        const copyCenterFromEnd = scrollyBottom - (copyRect.top + copyRect.height / 2);
        const imageCenterFromStickyBottom =
          stickyRect.bottom - (imageRect.top + imageRect.height / 2);

        scrolly.style.setProperty(
          "--scrolly-image-release",
          `${Math.max(0, copyCenterFromEnd - imageCenterFromStickyBottom)}px`,
        );
      };

      updateDesktopLayout();
      ScrollTrigger.addEventListener("refreshInit", updateDesktopLayout);

      panels.forEach((panel, index) => {
        triggers.push(
          ScrollTrigger.create({
            trigger: panel,
            start: "top center",
            end: "bottom center",
            onEnter: () => runActivate(index),
            onEnterBack: () => runActivate(index),
          }),
        );
      });

      return () => {
        triggers.forEach((trigger) => trigger.kill());
        ScrollTrigger.removeEventListener("refreshInit", updateDesktopLayout);
        scrolly.style.removeProperty("--scrolly-heading-height");
        scrolly.style.removeProperty("--scrolly-image-release");
        panels[panels.length - 1]?.style.removeProperty("padding-top");
      };
    }

    const syncMobileStep = () => {
      const bandTop = window.innerHeight * 0.55;
      const bandBottom = window.innerHeight * 0.95;
      let nextIndex = 0;
      let bestOverlap = -1;

      panels.forEach((panel, index) => {
        const { top, bottom } = panel.getBoundingClientRect();
        const overlap = Math.min(bottom, bandBottom) - Math.max(top, bandTop);

        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          nextIndex = index;
        }
      });

      runActivate(nextIndex);
    };

    const onScroll = () => syncMobileStep();

    triggers.push(
      ScrollTrigger.create({
        trigger: scrolly,
        start: "top bottom",
        end: "bottom top",
        onUpdate: syncMobileStep,
        onRefresh: syncMobileStep,
      }),
    );

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    syncMobileStep();

    return () => {
      triggers.forEach((trigger) => trigger.kill());
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  };

  const mm = gsap.matchMedia();

  mm.add("(min-width: 768px)", () => createTriggers(true));
  mm.add("(max-width: 767px)", () => createTriggers(false));

  const pending = layers.filter(
    (el) => el instanceof HTMLImageElement && !el.complete,
  );
  if (pending.length) {
    let left = pending.length;
    pending.forEach((img) => {
      img.addEventListener(
        "load",
        () => {
          left -= 1;
          if (left === 0) ScrollTrigger.refresh();
        },
        { once: true },
      );
    });
  }
}

export function initHomeAbout() {
  document.querySelectorAll("[data-scrolly-reveal]").forEach(initScrollyReveal);
}
